// PeriodSelector — compact v2 (Feb 2026).
//
// The previous chip-with-labels was too wide and pushed the last top-nav
// items off-screen. This version renders a small "Feb '26" pill that
// pops a lightweight popover to change month / year. Real estate on the
// top bar is preserved for the actual navigation.

import { useState, useRef, useEffect } from "react";
import { useMonth, MONTH_NAMES } from "../contexts/MonthContext";
import { ArrowCounterClockwise, Calendar, CaretDown } from "@phosphor-icons/react";

export default function PeriodSelector() {
  const { period, setPeriod, isCurrent, isFuture, goToCurrent } = useMonth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const thisYear = new Date().getFullYear();
  const yearList = [];
  for (let y = thisYear - 5; y <= thisYear + 1; y++) yearList.push(y);

  useEffect(() => {
    const h = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, []);

  // Status tone dot next to the pill so operators can tell whether the
  // period they're looking at is current / past / future at a glance
  // without needing wide text labels.
  const dotColor = isFuture ? "#B45309"    // amber-warm
                : !isCurrent ? "#D4A843"   // golden = past
                : "#0F766E";               // teal = current

  const short = `${MONTH_NAMES[period.month - 1].slice(0, 3)} '${String(period.year).slice(-2)}`;

  return (
    <div ref={rootRef} className="relative" data-testid="period-selector">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="h-9 flex items-center gap-1.5 px-2.5 rounded-lg text-xs font-bold whitespace-nowrap transition-colors text-slate-100 hover:bg-white/10"
        data-testid="period-selector-toggle"
      >
        <span className="w-2 h-2 rounded-full" style={{ background: dotColor }} />
        <Calendar size={14} weight="bold" className="text-[#D4A843]" />
        <span>{short}</span>
        <CaretDown size={10} weight="bold" className="opacity-70" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 min-w-[220px] bg-white rounded-lg shadow-2xl border border-[#E2E8F0] z-50 p-3 space-y-2">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#94A3B8]">Working Period</div>
          <div className="grid grid-cols-2 gap-2">
            <select
              value={period.month}
              onChange={(e) => setPeriod({ ...period, month: parseInt(e.target.value, 10) })}
              className="min-h-[36px] border border-[#E2E8F0] rounded px-2 text-xs font-bold text-[#1D2951] bg-white"
              data-testid="period-month"
            >
              {MONTH_NAMES.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
            </select>
            <select
              value={period.year}
              onChange={(e) => setPeriod({ ...period, year: parseInt(e.target.value, 10) })}
              className="min-h-[36px] border border-[#E2E8F0] rounded px-2 text-xs font-bold text-[#1D2951] bg-white"
              data-testid="period-year"
            >
              {yearList.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="flex items-center justify-between pt-1.5 border-t border-[#F1F5F9]">
            <span className="text-[10px] font-black uppercase tracking-wider"
              style={{ color: dotColor }}>
              {isCurrent ? "Current" : isFuture ? "Future" : "Past"}
            </span>
            {!isCurrent && (
              <button onClick={() => { goToCurrent(); setOpen(false); }}
                className="text-[10px] font-bold text-[#1D2951] hover:text-[#D4A843] flex items-center gap-1"
                data-testid="period-go-current">
                <ArrowCounterClockwise size={12} weight="bold" /> Jump to current
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
